import { useEffect, useRef, useState } from 'react'
import { PlusIcon } from '@phosphor-icons/react'
import styles from './FilterTab.module.scss'
import { unpackFilters } from '../../../../Insights/Filter'
import dayjs from 'dayjs'
import { FilterItem } from '../../components/FilterItem/FilterItem'
import { FilterBox } from '../FilterBox'
import { ScoopMenu, ScoopMenuItem, ScoopText, ScoopChip } from '../../../../ui'
import { useModal, MODALS } from '../../../../modals'

export const FilterTab = ({
    getWorkspaceMetadata,
    workspaceMetadata,
    initialSelectedColumn,
    initialOpen,
    analyzeChanges,
    chartState,
    setConfig,
    server,
    onOpenTimeRange,
    config,
}) => {
    const [selectedFilterColumn, setSelectedFilterColumn] = useState(null)
    const [editState, setEditState] = useState(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const summaryFiltersRef = useRef()

    // Modal Manager hooks
    const addFilterModal = useModal(MODALS.EXPLORER.ADD_FILTER)
    const savedFilterModal = useModal(MODALS.EXPLORER.SAVED_FILTER)

    useEffect(() => {
        setSelectedFilterColumn(initialSelectedColumn !== undefined ? initialSelectedColumn : null)
        if (initialOpen) {
            addFilterModal.show({
                config,
                setConfig,
                chartProperties: chartState,
                analyzeChanges,
                saveFilter,
                editState: null,
            })
        }
    }, [initialSelectedColumn, initialOpen])

    const getCustomTimeFilterString = () => {
        let result = 'Date: '
        if (config.from) result += 'from ' + dayjs(config.from).format('MM/DD/YYYY')
        if (config.to) {
            if (config.from) result += ' '
            result += 'to ' + dayjs(config.to).format('MM/DD/YYYY')
        }
        return result
    }

    const deleteFilterClick = (event, filter) => {
        if (filter === 'Custom') {
            config.timeRange = 'Last Year'
            config.from = null
            config.to = null
        } else {
            config.filter = chartState.removeFilterItem(filter)
            config.usedDrillAttributes = config.usedDrillAttributes.filter(
                (att) => att !== filter.attributeName
            )
        }
        chartState.getResults(config)
        setConfig({ ...config })
    }

    const getColumnType = (attributeName) => {
        const selectedItem = config.selectedItems.find(
            (item) =>
                (item.kpi && item.kpi === attributeName) ||
                (item.column && item.column === attributeName)
        )
        if (selectedItem) {
            const table = workspaceMetadata?.inboxes?.forEach((inbox) => {
                return inbox.tables.find(
                    (table) => table.reportSeriesTableID === selectedItem.reportSeriesTableID
                )
            })
            return table.columns.find((column) => column.columnName === attributeName).columnType
        }
    }

    const editFilterClick = (f) => {
        setEditState(f)
        setSelectedFilterColumn({
            columnName: f.attributeName,
            columnType: getColumnType(f.attributeName),
            filterValue: f.filterValue.values,
        })
        addFilterModal.show({
            config,
            setConfig,
            chartProperties: chartState,
            analyzeChanges,
            saveFilter,
            editState: f,
        })
    }

    const handleSaveFilterClick = (f) => {
        savedFilterModal.show({
            filterToSave: f,
            server,
            workspaceMetadata,
            getWorkspaceMetadata,
            config,
            setConfig,
        })
    }

    const isActiveFilter = (f) => {
        if (config.selectedItems.length === 0 || !config.selectedItems.some((i) => i.kpi))
            return false
        const usedKpis = workspaceMetadata.kpis
            .filter((kpi) => config.selectedItems.some((i) => i.kpi === kpi.metricName))
            .map((kpi) => ({ metricName: kpi.metricName, savedFilter: kpi.savedFilterID }))
        if (usedKpis.length === 0) return false
        return usedKpis.find((kpi) => kpi.savedFilter === f.savedFilterKey)
    }

    const unpackedFilters = unpackFilters(chartState.config.filter)

    const saveFilter = (filterName, filter) => {
        server.postData(
            {
                action: 'putObject',
                class: 'scoop.queryfilter.SavedFilter',
                value: {
                    filterName: filterName,
                    workspaceID: server.workspaceID,
                    filter: filter,
                },
            },
            () => {
                getWorkspaceMetadata()
            }
        )
    }

    const noMetricsSelected = () => {
        if (config.view === 'table') {
            return config.selectedTableColumns.length === 0 && config.selectedTableKpis.length === 0
        } else {
            return config.selectedItems.length === 0
        }
    }

    return (
        <>
            <div className={styles.container}>
                <div className={styles.sectionHeader}>
                    <ScoopText className={styles.sectionTitle}>Summary filters</ScoopText>
                    {!noMetricsSelected() && (
                        <button
                            className={styles.addButton}
                            ref={summaryFiltersRef}
                            onClick={() => {
                                setSelectedFilterColumn(null)
                                setMenuOpen(true)
                            }}
                        >
                            <PlusIcon size={18} />
                        </button>
                    )}
                </div>
                <div className={styles.filtersContainer}>
                    {noMetricsSelected() &&
                        (!config.filter || config.filter?.filters?.length === 0) && (
                            <div className={styles.emptyState}>
                                <ScoopText className={`inter ${styles.emptyStateTitle}`}>
                                    No source metrics selected
                                </ScoopText>
                                <ScoopText className={`inter ${styles.emptyStateSubtitle}`}>
                                    Select a source metric from data inputs
                                </ScoopText>
                            </div>
                        )}
                    {!noMetricsSelected() && unpackedFilters?.length === 0 && (
                        <div className={styles.emptyState}>
                            <ScoopText className={`inter ${styles.emptyStateTitle}`}>
                                No filters applied
                            </ScoopText>
                            <ScoopText className={`inter ${styles.emptyStateSubtitle}`}>
                                Click <span style={{ fontSize: '18px' }}> + </span> to get started
                                or select a saved filter
                            </ScoopText>
                        </div>
                    )}
                    {config.timeRange === 'Custom' && (config.from || config.to) && (
                        <ScoopChip
                            key={'Time'}
                            label={getCustomTimeFilterString()}
                            onDelete={(event) => deleteFilterClick(event, 'Custom')}
                            onClick={onOpenTimeRange}
                            className={styles.filterChip}
                        />
                    )}
                    <div className={styles.filtersList}>
                        {unpackedFilters?.map((f, i) => {
                            return (
                                <FilterBox
                                    key={i}
                                    filter={f}
                                    number={i + 1}
                                    handleRemove={(event) => deleteFilterClick(event, f)}
                                    handleEdit={() => editFilterClick(f)}
                                    handleSave={() => handleSaveFilterClick(f)}
                                />
                            )
                        })}
                    </div>
                </div>
            </div>
            <hr className={styles.divider} />
            <div className={styles.savedFiltersContainer}>
                <div className={styles.sectionHeader}>
                    <ScoopText className={`inter ${styles.sectionTitle}`}>Saved filters</ScoopText>
                </div>
                <div className={styles.savedFiltersList}>
                    {workspaceMetadata != null &&
                        workspaceMetadata.filters.map((f) => (
                            <FilterItem
                                key={f.savedFilterKey}
                                filter={f}
                                server={server}
                                getWorkspaceMetadata={getWorkspaceMetadata}
                                isActive={isActiveFilter(f)}
                                config={config}
                                setConfig={setConfig}
                                chartState={chartState}
                            />
                        ))}
                </div>
            </div>
            <ScoopMenu
                open={menuOpen}
                anchorEl={summaryFiltersRef.current}
                onClose={() => setMenuOpen(false)}
            >
                <ScoopMenuItem
                    onClick={() => {
                        addFilterModal.show({
                            config,
                            setConfig,
                            chartProperties: chartState,
                            analyzeChanges,
                            saveFilter,
                            editState: null,
                        })
                        setMenuOpen(false)
                    }}
                >
                    Create new filter
                </ScoopMenuItem>
                {unpackedFilters?.length > 0 && (
                    <ScoopMenuItem
                        onClick={() => {
                            savedFilterModal.show({
                                filterToSave: chartState.config.filter,
                                server,
                                workspaceMetadata,
                                getWorkspaceMetadata,
                                config,
                                setConfig,
                            })
                            setMenuOpen(false)
                        }}
                    >
                        Save filter
                    </ScoopMenuItem>
                )}
            </ScoopMenu>
        </>
    )
}
